import { InventoryEventOption, InventoryItem } from '../types';

export interface PreviousEventWithItems {
  event: InventoryEventOption;
  items: InventoryItem[];
}

const matchesEvent = (
  record: { eventId?: string; eventLabel?: string },
  eventId: string,
  eventLabel: string
) => record.eventId === eventId || (!record.eventId && record.eventLabel === eventLabel);

/**
 * Walks backward from the selected event through chronologically earlier events
 * (activeEventOptions must be sorted ascending by start time) and returns the
 * nearest one that actually has inventory items, skipping empty ones in between.
 */
export const findPreviousEventWithItems = (
  activeEventOptions: InventoryEventOption[],
  items: InventoryItem[],
  selectedEventId: string
): PreviousEventWithItems | null => {
  if (!selectedEventId) return null;
  const currentIndex = activeEventOptions.findIndex(option => option.id === selectedEventId);
  if (currentIndex <= 0) return null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const candidate = activeEventOptions[i];
    const candidateItems = items.filter(item => matchesEvent(item, candidate.id, candidate.label));
    if (candidateItems.length > 0) {
      return { event: candidate, items: candidateItems };
    }
  }
  return null;
};

/**
 * Field-mapping for carrying an item into a new event. Expiry date is
 * intentionally dropped — a new mission's stock needs a freshly checked date.
 */
export const buildCopiedInventoryItemData = (
  source: InventoryItem,
  targetEventId: string,
  targetEventLabel: string,
  actor: string,
  now: string
): Omit<InventoryItem, 'id'> => ({
  eventId: targetEventId,
  eventLabel: targetEventLabel,
  department: source.department,
  name: source.name,
  quantity: source.quantity,
  unit: source.unit,
  note: source.note,
  isDeleted: false,
  createdAt: now,
  createdBy: actor,
  updatedAt: now,
  updatedBy: actor,
});
